//* 삭제한(소프트 삭제) 주문을 주문자 번호로 찾아 되살리는 영역.
//* 삭제 주문은 일반 목록 조회에서 빠지므로 전용 검색 경로로만 조회된다.
import { FC, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { SearchIcon } from '@heroicons/react/solid';
import {
  patchOrderWaitingToPaid,
  restoreShopOrderForSeller,
  searchDeletedShopOrderListForSeller,
} from '@shared/api/shopOrder/shopOrder.controller';
import {
  shopOrderNotPaidListForSellerByShopUrl,
  shopOrderWaitingListForSellerByShopUrl,
} from '@shared/api/shopOrder/shopOrder.hook';
import { ShopOrderForSeller } from '@shared/api/shopOrder/shopOrder.interface';
import { Button } from '@shared/components/Button';
import ConfirmModal from '@shared/components/ConfirmModal';
import { Loading } from '@shared/components/Loading';
import {
  notificateError,
  notificateSuccess,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { PaymentState } from '@shared/types';
import { formatDate } from '@shared/utils/formatDate';
import { paymentStatusLabel } from '@shared/utils/orderStatusLabel';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';

//* 표기는 @shared/utils/orderStatusLabel 공용 사전을 쓰되, 이 화면은 "복원이 필요한 상태"를
//* 드러내야 해서 두 항목만 덮는다 — 삭제 후 가상계좌 입금이 들어오면 웹훅이 Paid로 바꾼다(docStatus는 그대로).
const paymentLabelOverrides = {
  [PaymentState.PAID]: '입금완료',
  [PaymentState.CONFIRMED]: '발송완료',
};

//* 지운 뒤 결제가 끝난 주문 — 복원 안 하면 발송목록에 안 뜬다
const isPaidAfterDelete = (status: string) =>
  status === PaymentState.PAID || status === PaymentState.CONFIRMED;

//* 삭제 주문 요청 실패 사유는 서버 문구가 가장 정확해서 400이면 그대로 보여준다
const errorMessage = (err: any, fallback: string) => {
  const status = err?.response?.status;
  if (status === 400) return err?.response?.data?.message ?? fallback;
  if (status === 404) return '아직 서버에 반영되지 않은 기능입니다.';
  if (status === 401 || status === 403) return '권한이 없습니다.';
  return fallback;
};

interface CardProps {
  shopOrder: ShopOrderForSeller;
  //* 처리된 주문은 검색 결과에서 빼서 두 번 누르는 실수를 막는다
  onProcessed: (shopOrderId: string) => void;
}

const DeletedShopOrderCard: FC<CardProps> = function ({ shopOrder, onProcessed }) {
  const { shopId } = useParams();
  const { sellerRole } = useAuth();
  const queryClient = useQueryClient();
  const notifiacationInstance = useRecoilValue(notificationInstanceAtom);

  const paidOpenId = `deletedShopOrderPaid-${shopOrder._id}`;
  const restoreOpenId = `deletedShopOrderRestore-${shopOrder._id}`;
  const setPaidOpen = useSetRecoilState(simpleOpenAtomFamily(paidOpenId));
  const setRestoreOpen = useSetRecoilState(simpleOpenAtomFamily(restoreOpenId));

  const deliveryDetail = shopOrder.deliveryDetail;
  const isWaiting = shopOrder.paymentDetail.status === PaymentState.WAITING;
  const paidAfterDelete = isPaidAfterDelete(shopOrder.paymentDetail.status);

  //* 계좌·가상계좌 두 목록 모두 되살아날 수 있으므로 함께 무효화한다
  const invalidateWaitingList = async () => {
    await Promise.all([
      queryClient.invalidateQueries(
        shopOrderWaitingListForSellerByShopUrl(`${shopId}`, sellerRole()),
      ),
      queryClient.invalidateQueries(shopOrderNotPaidListForSellerByShopUrl(sellerRole())),
    ]);
  };

  //* 입금완료 처리 — 서버가 docStatus까지 Active로 되돌리므로 복원 호출이 따로 필요 없다
  const onPaidClick = async () => {
    try {
      await patchOrderWaitingToPaid(shopOrder._id, sellerRole());
      await invalidateWaitingList();
      notificateSuccess(notifiacationInstance, '입금완료 처리 및 복원 완료');
      onProcessed(shopOrder._id);
    } catch (err: any) {
      console.error(err);
      notificateError(
        notifiacationInstance,
        errorMessage(err, '입금완료 처리에 실패했습니다.'),
      );
    }
  };

  const onRestoreClick = async () => {
    try {
      await restoreShopOrderForSeller(shopOrder._id, sellerRole());
      await invalidateWaitingList();
      notificateSuccess(notifiacationInstance, '주문 복원 완료');
      onProcessed(shopOrder._id);
    } catch (err: any) {
      console.error(err);
      notificateError(notifiacationInstance, errorMessage(err, '복원에 실패했습니다.'));
    }
  };

  return (
    <div className="rounded-lg border border-gray-300 bg-gray-50 p-4 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="w-0 flex-1 space-y-0.5">
          <p className="truncate font-medium text-gray-900">
            {shopOrder.orderDetail.shopItemEmbed.name}{' '}
            <span className="text-sm font-normal text-gray-600">
              {shopOrder.orderDetail.shopItemEmbed.priceRetail.toLocaleString()}원
            </span>
          </p>
          <p className="text-sm text-gray-700">
            주문자: {deliveryDetail.senderName} {deliveryDetail.senderPhoneNumber}
          </p>
          <p className="truncate text-sm text-gray-700">
            배송지: {deliveryDetail.receiverAddress}
          </p>
          <p className="text-xs text-gray-500">
            주문{' '}
            {formatDate(shopOrder.paymentDetail.requestedAt, {
              dateSeparater: '.',
              contains: { year: false, day: true, hours: true, minutes: true },
            })}
            {shopOrder.deletedAt && (
              <>
                {' · 삭제 '}
                {formatDate(shopOrder.deletedAt, {
                  dateSeparater: '.',
                  contains: { year: false, day: true, hours: true, minutes: true },
                })}
              </>
            )}
          </p>
          {shopOrder.deleteReason && (
            <p className="text-xs text-gray-500">삭제사유: {shopOrder.deleteReason}</p>
          )}
        </div>
        <p
          className={`shrink-0 rounded px-2 py-0.5 text-xs font-medium ${
            paidAfterDelete ? 'bg-red-100 text-red-700' : 'bg-gray-200 text-gray-700'
          }`}
        >
          {paymentStatusLabel(shopOrder.paymentDetail.status, paymentLabelOverrides)}
        </p>
      </div>

      {paidAfterDelete && (
        <p className="mt-2 break-keep rounded bg-red-50 p-2 text-xs text-red-700">
          삭제한 뒤 결제가 완료된 주문입니다. 복원해야 발송목록에 나타납니다.
        </p>
      )}

      <div className="mt-3 flex justify-end gap-2">
        <Button
          className="button-sm border border-gray-400 bg-white text-gray-700 hover:bg-gray-100"
          onClick={() => {
            setRestoreOpen(true);
          }}
        >
          복원만
        </Button>
        {isWaiting && (
          <Button
            className="button-sm bg-cyan-600 text-white hover:bg-cyan-700"
            onClick={() => {
              setPaidOpen(true);
            }}
          >
            입금완료
          </Button>
        )}
      </div>

      <ConfirmModal
        openId={restoreOpenId}
        buttonTitle="복원"
        title="주문 복원"
        subtitle="삭제를 취소하고 원래 목록으로 되돌립니다."
        onClick={onRestoreClick}
      ></ConfirmModal>
      <ConfirmModal
        openId={paidOpenId}
        buttonTitle="확인"
        title="입금완료확인"
        subtitle="주문을 되살리고 입금완료로 처리합니다. 구매자에게 결제완료 알림이 발송됩니다."
        onClick={onPaidClick}
      ></ConfirmModal>
    </div>
  );
};

export const DeletedShopOrderSearchSection: FC = function () {
  const { sellerRole } = useAuth();
  const notifiacationInstance = useRecoilValue(notificationInstanceAtom);

  const [phoneNumber, setPhoneNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [shopOrderList, setShopOrderList] = useState<ShopOrderForSeller[] | undefined>(
    undefined,
  );

  const onSearch = async () => {
    if (phoneNumber.replace(/\D/g, '').length < 4) {
      notificateError(notifiacationInstance, '주문자 번호를 4자리 이상 입력해주세요.');
      return;
    }
    setIsLoading(true);
    try {
      const shopOrderList = (await searchDeletedShopOrderListForSeller(
        phoneNumber,
        sellerRole(),
      )) as unknown as ShopOrderForSeller[];
      setShopOrderList(shopOrderList);
    } catch (err: any) {
      console.error(err);
      notificateError(notifiacationInstance, errorMessage(err, '검색에 실패했습니다.'));
    } finally {
      setIsLoading(false);
    }
  };

  const onProcessed = (shopOrderId: string) => {
    setShopOrderList((prev) => prev?.filter((e) => e._id !== shopOrderId));
  };

  return (
    <div className="mt-8 space-y-4">
      <p className="w-full font-bold">
        삭제한 내역 검색
        <span className="font-normal">
          (주문자 번호로 찾아 입금완료 처리하면 목록으로 되살아납니다)
        </span>
      </p>

      <div className="space-y-1.5">
        <div className="relative flex w-full items-center theme-text-8 focus-within:theme-text-9 sm:w-96">
          <input
            id="deleted-order-search-field"
            name="deleted-order-search-field"
            className="block h-full w-full border py-4 pl-4 pr-3 placeholder-gray-500 theme-text-9 sm:text-sm"
            placeholder="주문자 번호 (뒷 4자리도 가능)"
            type="search"
            value={phoneNumber}
            onChange={(e) => {
              setPhoneNumber(e.currentTarget.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onSearch();
            }}
          />
          <div
            className="h-full cursor-pointer border-y border-r p-[15px] theme-bg-main theme-border-main"
            onClick={onSearch}
          >
            <SearchIcon className="h-5 w-5 theme-text-4" aria-hidden="true" />
          </div>
        </div>
        <p className="break-keep text-xs text-gray-500">
          보내는 분·받는 분 연락처로 검색합니다. 삭제한 주문만 나옵니다.
        </p>
      </div>

      {isLoading && (
        <div className="center-box">
          <Loading />
        </div>
      )}

      {!isLoading && shopOrderList && shopOrderList.length === 0 && (
        <p className="text-sm text-gray-500">검색된 삭제 주문이 없습니다.</p>
      )}

      {!isLoading && shopOrderList && shopOrderList.length > 0 && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {shopOrderList.map((shopOrder) => (
            <DeletedShopOrderCard
              key={shopOrder._id}
              shopOrder={shopOrder}
              onProcessed={onProcessed}
            />
          ))}
        </div>
      )}
    </div>
  );
};
